import React, { useState, useEffect, useRef } from 'react';
import { collection, query, getDocs, updateDoc, doc, serverTimestamp, addDoc, onSnapshot, orderBy, setDoc } from 'firebase/firestore';
import EmailEditor from 'react-email-editor';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { sanitizeString } from '../../utils/sanitization';
import { useAuth } from '../auth/AuthContext';
import { API_URL } from '../../utils/apiConfig';
import { logUserAction } from '../../utils/logger';
import { generateTemplateId } from '../../utils/documentIdUtils';
import { FEATURE_PERMISSIONS } from '../../utils/permissions';
import { usePermissions } from '../../hooks/usePermissions';
import { canManageEmailTemplate, canViewAllEmail } from '../../utils/emailAccess';
import {
  EMAIL_TEMPLATE_VARIABLES,
  extractTemplateVariables,
  findUnknownVariables,
  formatUnknownVariablesMessage
} from '../../utils/emailTemplateVariables';

const FIELD_LABELS = {
  subject: 'Subject Line',
  preheader: 'Preheader Text',
  htmlContent: 'HTML Content',
  textContent: 'Plain Text Version'
};

const TemplateEditor = ({ template, onClose, onSaved }) => {  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  const emailEditorRef = useRef(null);
  const editorContainerRef = useRef(null); // Reference to the editor container div
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    subject: '',
    preheader: '',
    htmlContent: '',
    textContent: '',
    designJson: null, // Store Unlayer design JSON
    category: 'promotional',
    variables: [],
    isPrivate: false, // Private templates only visible to creator
    ...template
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editorMode, setEditorMode] = useState('design'); // design, code, preview
  const [showVariableMenu, setShowVariableMenu] = useState(false);
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(''); // Store preview HTML
  const htmlEditorRef = useRef(null); // Reference to HTML textarea for cursor position
  const subjectRef = useRef(null);
  const preheaderRef = useRef(null);
  const textEditorRef = useRef(null);
  const lastFocusRef = useRef({ field: 'subject', start: null, end: null });
  const [insertTarget, setInsertTarget] = useState('subject');
  const fieldRefs = {
    subject: subjectRef,
    preheader: preheaderRef,
    htmlContent: htmlEditorRef,
    textContent: textEditorRef
  };
  const [categories, setCategories] = useState(['promotional', 'newsletter', 'transactional', 'announcement']);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Available variables (canonical list shared with the campaign sender)
  const availableVariables = EMAIL_TEMPLATE_VARIABLES;

  const unknownVariables = findUnknownVariables(
    formState.subject,
    formState.htmlContent,
    formState.textContent
  );

  const usedVariables = extractTemplateVariables(
    formState.subject,
    formState.htmlContent,
    formState.textContent
  );

  // The HTML textarea only exists in code mode; the designer owns the markup otherwise
  const activeInsertField = insertTarget === 'htmlContent' && editorMode !== 'code' ? 'subject' : insertTarget;

  // Default HTML template
  const defaultTemplate = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{subject}}</title>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background-color: #4F46E5; color: white; padding: 20px; text-align: center; }
    .content { background-color: #ffffff; padding: 30px; }
    .footer { background-color: #f3f4f6; padding: 20px; text-align: center; font-size: 12px; color: #6b7280; }
    .button { display: inline-block; padding: 12px 24px; background-color: #4F46E5; color: white; text-decoration: none; border-radius: 4px; margin: 10px 0; }
    .button:hover { background-color: #4338CA; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Sofibella Activewear</h1>
    </div>
    <div class="content">
      <p>Hello {{customerName}},</p>
      
      <p>We're excited to share our latest collection with {{customerName}}!</p>
      
      <p>As a valued partner in {{city}}, {{state}}, we wanted to give you first access to our new products.</p>
      
      <p style="text-align: center;">
        <a href="https://sofibella.com/collections/new" class="button">View Collection</a>
      </p>
      
      <p>Thank you for your continued partnership!</p>
      
      <p>Best regards,<br>The Sofibella Team</p>
    </div>
    <div class="footer">
      <p>&copy; {{currentYear}} Sofibella Activewear. All rights reserved.</p>
      <p>You're receiving this email because you're a valued customer.</p>
      <p><a href="#" style="color: #4F46E5;">Unsubscribe</a></p>
    </div>
  </div>
</body>
</html>`;

  useEffect(() => {
    // Load categories from Firestore with real-time updates
    const categoriesQuery = query(
      collection(db, collectionPath('templateCategories')),
      orderBy('createdAt', 'asc')
    );
    
    const unsubscribe = onSnapshot(
      categoriesQuery,
      (snapshot) => {
        const defaultCategories = ['promotional', 'newsletter', 'transactional', 'announcement'];
        const customCategories = snapshot.docs.map(doc => doc.data().name);
        const allCategories = [...defaultCategories, ...customCategories];
        setCategories(allCategories);
        setLoadingCategories(false);
      },
      (error) => {
        console.error('Failed to load categories:', error);
        // Fallback to defaults only
        setCategories(['promotional', 'newsletter', 'transactional', 'announcement']);
        setLoadingCategories(false);
      }
    );
    
    // If creating new template and no HTML content, use default
    if (!template?.id && !formState.htmlContent) {
      setFormState(prev => ({ ...prev, htmlContent: defaultTemplate }));
    }

    // Extract variables from content
    extractVariables();
    
    return () => unsubscribe();
  }, []);

  const extractVariables = () => {
    const variableRegex = /\{\{(\w+)\}\}/g;
    const foundVariables = new Set();
    
    // Extract from subject
    let match;
    while ((match = variableRegex.exec(formState.subject)) !== null) {
      foundVariables.add(match[1]);
    }
    
    // Extract from HTML content
    variableRegex.lastIndex = 0;
    while ((match = variableRegex.exec(formState.htmlContent)) !== null) {
      foundVariables.add(match[1]);
    }
    
    // Extract from text content
    variableRegex.lastIndex = 0;
    while ((match = variableRegex.exec(formState.textContent)) !== null) {
      foundVariables.add(match[1]);
    }

    setFormState(prev => ({
      ...prev,
      variables: Array.from(foundVariables)
    }));
  };

  const handleInputChange = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Handle adding a new custom category to Firestore
  const handleAddCategory = async () => {
    const trimmedName = newCategoryName.trim();
    
    // Validation
    if (!trimmedName) {
      setError('Category name cannot be empty');
      return;
    }
    
    if (trimmedName.length > 30) {
      setError('Category name must be 30 characters or less');
      return;
    }
    
    // Check for duplicates (case-insensitive)
    const normalizedName = trimmedName.toLowerCase();
    if (categories.some(cat => cat.toLowerCase() === normalizedName)) {
      setError('Category already exists');
      return;
    }
    
    try {
      // Save to Firestore
      await addDoc(collection(db, collectionPath('templateCategories')), {
        name: trimmedName,
        createdBy: currentUser?.uid || 'unknown',
        createdByName: currentUser?.displayName || 'Unknown User',
        createdAt: serverTimestamp(),
        isDefault: false
      });
      
      // Auto-select the new category
      setFormState(prev => ({ ...prev, category: trimmedName }));
      
      // Reset add category UI
      setNewCategoryName('');
      setShowAddCategory(false);
      setError('');
    } catch (err) {
      console.error('Failed to add category:', err);
      setError('Failed to add category: ' + err.message);
    }
  };

  // Tracks where a variable should be inserted: the last text field the user touched
  const trackCursor = (field) => (e) => {    lastFocusRef.current = {
      field,
      start: e.target.selectionStart,
      end: e.target.selectionEnd
    };
    setInsertTarget(field);
  };

  const insertVariable = (variableName, targetField = null) => {
    const variableTag = `{{${variableName}}}`;
    const last = lastFocusRef.current;
    const field = targetField || activeInsertField;
    const element = fieldRefs[field]?.current || null;

    const currentValue = formState[field] || '';
    const useCursor = !targetField && field === last.field && Number.isInteger(last.start);
    const startPos = useCursor ? last.start : currentValue.length;
    const endPos = useCursor ? last.end : currentValue.length;
    const newValue = currentValue.substring(0, startPos) + variableTag + currentValue.substring(endPos);
    const caret = startPos + variableTag.length;

    setFormState(prev => ({ ...prev, [field]: newValue }));
    lastFocusRef.current = { field, start: caret, end: caret };

    setTimeout(() => {
      if (element) {
        element.focus();
        element.setSelectionRange(caret, caret);
      }
    }, 0);
  };

  const generateTextFromHtml = async () => {
    // Get current HTML - export from editor if in design mode
    let htmlToConvert = formState.htmlContent;
    
    if (editorMode === 'design' && emailEditorRef.current && editorLoaded) {
      try {
        const { html } = await exportHtmlFromEditor();
        htmlToConvert = html;
      } catch (err) {
        console.error('Failed to export for text generation:', err);
        // Fall back to stored HTML
      }
    }
    
    // Strip HTML tags and convert to plain text
    const text = htmlToConvert
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\n\s*\n/g, '\n\n')
      .trim();

    setFormState(prev => ({ ...prev, textContent: text }));
  };

  // Unlayer EmailEditor handlers
  const onEditorLoad = () => {
    console.log('Email editor loaded');
    setEditorLoaded(true);
    
    // Load existing design if available
    if (formState.designJson && emailEditorRef.current) {
      emailEditorRef.current.editor.loadDesign(formState.designJson);
    }
    
    // Apply dynamic height adjustment to Unlayer iframe
    adjustUnlayerIframeHeight();
  };
  
  // Dynamic iframe height adjustment
  const adjustUnlayerIframeHeight = () => {
    if (!editorContainerRef.current) return;
    
    // Find the Unlayer iframe inside the container
    const iframe = editorContainerRef.current.querySelector('iframe');
    if (!iframe) {
      // Retry after a short delay if iframe not found yet
      setTimeout(adjustUnlayerIframeHeight, 100);
      return;
    }
    
    console.log('Found Unlayer iframe, applying dynamic height');
    
    // Get the container height
    const containerHeight = editorContainerRef.current.clientHeight;
    
    // Force the iframe to fill the container
    iframe.style.height = `${containerHeight}px`;
    iframe.style.width = '100%';
    iframe.style.minHeight = `${containerHeight}px`;
    iframe.style.maxHeight = `${containerHeight}px`;
    iframe.style.border = 'none';
    iframe.style.display = 'block';
    
    // Also try to find the wrapper div that react-email-editor creates
    const editorWrapper = editorContainerRef.current.querySelector('div');
    if (editorWrapper) {
      editorWrapper.style.height = '100%';
      editorWrapper.style.minHeight = '100%';
    }
    
    console.log('Adjusted iframe height to:', containerHeight);
    
    // Try to communicate with iframe to adjust content height
    // Note: This may be limited by cross-origin restrictions
    try {
      // Set up a resize observer to monitor container size changes
      const resizeObserver = new ResizeObserver(() => {
        if (editorContainerRef.current && iframe) {
          const newContainerHeight = editorContainerRef.current.clientHeight;
          iframe.style.height = `${newContainerHeight}px`;
          iframe.style.minHeight = `${newContainerHeight}px`;
          iframe.style.maxHeight = `${newContainerHeight}px`;
          console.log('Re-adjusted iframe height to:', newContainerHeight);
        }
      });
      
      resizeObserver.observe(editorContainerRef.current);
      
      // Return cleanup function
      return () => resizeObserver.disconnect();
    } catch (err) {
      console.warn('ResizeObserver not supported or error:', err);
    }
  };
  
  // Monitor fullscreen and editor mode changes to re-adjust iframe
  useEffect(() => {
    if (editorMode === 'design' && editorLoaded) {
      // Re-adjust iframe height when entering design mode or toggling fullscreen
      // Add a small delay to ensure the container has rendered
      const timeoutId = setTimeout(() => {
        const cleanup = adjustUnlayerIframeHeight();
        return cleanup;
      }, 150);
      
      return () => clearTimeout(timeoutId);
    }
  }, [editorMode, isFullscreen, editorLoaded]);

  const exportHtmlFromEditor = () => {
    return new Promise((resolve, reject) => {
      if (!emailEditorRef.current || !editorLoaded) {
        reject(new Error('Editor not loaded'));
        return;
      }

      emailEditorRef.current.editor.exportHtml((data) => {
        const { design, html } = data;
        resolve({ design, html });
      });
    });
  };

  // Clean undefined values from objects (recursively)
  const removeUndefinedValues = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    
    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefinedValues(item));
    }
    
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned;
  };

  // Sync design to state before switching tabs
  const syncDesignToState = async () => {
    if (!editorLoaded || !emailEditorRef.current) return;
    
    // Only sync if the template was originally created with designJson
    // Don't overwrite HTML-only templates with empty design exports
    if (!formState.designJson) {
      console.log('Skipping sync - template has no design JSON (HTML-only template)');
      return;
    }
    
    setSyncing(true);
    try {
      const { design, html } = await exportHtmlFromEditor();
      setFormState(prev => ({
        ...prev,
        htmlContent: html,
        designJson: design
      }));
    } catch (err) {
      console.error('Failed to sync design:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleTabChange = async (newMode) => {
    // Sync current design before switching
    if (editorMode === 'design' && editorLoaded) {
      await syncDesignToState();
    }
    
    // If switching TO preview mode, generate preview HTML
    if (newMode === 'preview') {
      await updatePreview();
    }
    
    // If switching TO design mode from code, reload the design from HTML
    if (newMode === 'design' && editorMode === 'code' && editorLoaded && emailEditorRef.current) {
      // Clear the design and let user rebuild, or try to parse HTML (not recommended)
      // For now, we'll just alert the user that code changes won't auto-convert to design
      console.log('Switched to design mode from code - manual design may be needed');
    }
    
    setEditorMode(newMode);
  };

  const updatePreview = async () => {
    // Get the current HTML - export from editor if in design mode
    let htmlToPreview = formState.htmlContent;
    
    if (editorMode === 'design' && emailEditorRef.current && editorLoaded) {
      try {
        const { html } = await exportHtmlFromEditor();
        htmlToPreview = html;
      } catch (err) {
        console.error('Failed to export for preview:', err);
        // Fall back to stored HTML
      }
    }
    
    // Replace variables with example data
    let processedHtml = htmlToPreview;
    availableVariables.forEach(v => {
      const regex = new RegExp(`\\{\\{${v.name}\\}\\}`, 'g');
      processedHtml = processedHtml.replace(regex, v.example);
    });
    
    setPreviewHtml(processedHtml);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const validateForm = () => {
    if (!formState.name?.trim()) {
      setError('Template name is required');
      return false;
    }
    if (!formState.subject?.trim()) {
      setError('Email subject is required');
      return false;
    }
    if (!formState.htmlContent?.trim()) {
      setError('Email content is required');
      return false;
    }
    // Unresolved tags are stripped at send time, so block typos before they ship
    const unknown = findUnknownVariables(
      formState.subject,
      formState.htmlContent,
      formState.textContent
    );
    if (unknown.length > 0) {
      setError(formatUnknownVariablesMessage(unknown));
      return false;
    }
    return true;
  };

  const handleSendTest = async () => {
    if (!currentUser?.email) {
      setError('No email address found for current user');
      return;
    }

    if (!formState.htmlContent?.trim() && !formState.textContent?.trim()) {
      setError('Template has no content to send');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Prepare test variables to replace in the template
      // currentDate and currentYear are handled by the server
      const testVariables = EMAIL_TEMPLATE_VARIABLES.reduce((acc, v) => {
        acc[v.name] = v.example;
        return acc;
      }, {});

      const response = await fetch(`${API_URL}/api/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: currentUser.email,
          subject: `[TEST] ${formState.subject || 'Test Email'}`,
          htmlContent: formState.htmlContent,
          textContent: formState.textContent,
          variables: testVariables
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send test email');
      }

      setError('');
      alert(`✅ Test email sent to ${currentUser.email}!\nCheck your inbox (including spam folder).`);
    } catch (err) {
      console.error('Send test error:', err);
      setError('Failed to send test email: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const permission = template?.id ? FEATURE_PERMISSIONS.EMAIL_EDIT_TEMPLATE : FEATURE_PERMISSIONS.EMAIL_CREATE_TEMPLATE;
    if (!canManageEmailTemplate(currentUser, can, template, permission)) {
      setError('You cannot edit this shared template.');
      return;
    }
    if (!validateForm()) return;

    setSaving(true);
    setError('');

    try {
      let htmlToSave = formState.htmlContent;
      let designToSave = formState.designJson;

      // If in design mode, export the HTML and design JSON from Unlayer
      if (editorMode === 'design' && emailEditorRef.current && editorLoaded) {
        try {
          const exported = await exportHtmlFromEditor();
          htmlToSave = exported.html;
          designToSave = exported.design;
        } catch (err) {
          console.error('Failed to export from editor:', err);
          setError('Failed to export design from editor');
          setSaving(false);
          return;
        }
      }

      // Re-check the exported HTML — design-mode edits aren't in formState yet
      const unknownInExport = findUnknownVariables(formState.subject, htmlToSave, formState.textContent);
      if (unknownInExport.length > 0) {
        setError(formatUnknownVariablesMessage(unknownInExport));
        setSaving(false);
        return;
      }

      // Re-extract variables before saving
      const tempState = { ...formState, htmlContent: htmlToSave };
      const variableRegex = /\{\{(\w+)\}\}/g;
      const foundVariables = new Set();
      let match;
      while ((match = variableRegex.exec(htmlToSave)) !== null) {
        foundVariables.add(match[1]);
      }
      
      // Convert to array and filter out any undefined/null values
      const variablesArray = Array.from(foundVariables).filter(v => v != null && v !== '');

      // Prepare sanitized data - ensure no undefined values
      const sanitized = {
        name: sanitizeString(formState.name || '', 200) || '',
        description: sanitizeString(formState.description || '', 500) || '',
        subject: sanitizeString(formState.subject || '', 200) || '',
        preheader: sanitizeString(formState.preheader || '', 200) || '',
        htmlContent: htmlToSave || '', // Don't sanitize HTML content
        textContent: formState.textContent || htmlToSave.replace(/<[^>]+>/g, '') || '', // Generate text version
        category: formState.category || 'promotional',
        variables: variablesArray,
        isArchived: Boolean(formState.isArchived), // Ensure boolean, not undefined
        isPrivate: Boolean(formState.isPrivate), // Ensure boolean, not undefined
        lastEditedBy: currentUser?.uid || '',
        lastEditedByName: currentUser?.displayName || currentUser?.email || 'Unknown',
        updatedAt: serverTimestamp()
      };

      // Debug: Check what we have
      console.log('Form state before sanitization:', {
        name: formState.name,
        subject: formState.subject,
        preheader: formState.preheader,
        category: formState.category,
        isArchived: formState.isArchived,
        htmlContentLength: htmlToSave?.length,
        textContentLength: formState.textContent?.length
      });

      // Only include designJson if it exists (Firestore doesn't like undefined)
      if (designToSave !== null && designToSave !== undefined) {
        // Clean any undefined values from the design JSON (Unlayer sometimes includes them)
        sanitized.designJson = removeUndefinedValues(designToSave);
      }

      if (template?.id) {
        // Update existing template
        // If this is a legacy template without createdBy, add it now
        if (!template.createdBy) {
          sanitized.createdBy = currentUser?.uid || '';
          sanitized.createdByName = currentUser?.displayName || currentUser?.email || 'Unknown';
        }
        
        await updateDoc(
          doc(db, collectionPath('emailTemplates'), template.id),
          sanitized
        );
        
        // Update local state with saved HTML and design
        setFormState(prev => ({
          ...prev,
          htmlContent: htmlToSave,
          designJson: designToSave,
          variables: variablesArray
        }));
        
        onSaved('Template updated successfully');
        await logUserAction({ action: 'Email Template Updated', details: `Template updated: "${formState.name}"`, entityType: 'EmailTemplate', entityId: template.id });
      } else {
        // Create new template - build clean object without undefined values
        const newTemplateData = {
          name: sanitized.name,
          subject: sanitized.subject,
          preheader: sanitized.preheader,
          htmlContent: sanitized.htmlContent,
          textContent: sanitized.textContent,
          category: sanitized.category,
          variables: sanitized.variables,
          isArchived: sanitized.isArchived,
          isPrivate: sanitized.isPrivate || false,
          createdBy: currentUser?.uid || '',
          createdByName: currentUser?.displayName || currentUser?.email || 'Unknown',
          lastEditedBy: currentUser?.uid || '',
          lastEditedByName: currentUser?.displayName || currentUser?.email || 'Unknown',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        // Only add designJson if it exists
        if (sanitized.designJson) {
          newTemplateData.designJson = sanitized.designJson; // Already cleaned by removeUndefinedValues
        }
        
        // Debug: Log the data we're about to save
        console.log('Creating template with data:', JSON.stringify(newTemplateData, (key, value) => {
          if (key === 'createdAt' || key === 'updatedAt') return '[Timestamp]';
          return value === undefined ? '[UNDEFINED!]' : value;
        }, 2));
        
        // More detailed check - log each field individually
        console.log('Detailed field check:');
        for (const [key, value] of Object.entries(newTemplateData)) {
          console.log(`  ${key}:`, typeof value, value === undefined ? '[UNDEFINED]' : value);
        }
        
        // Deep check for nested undefined in designJson
        if (newTemplateData.designJson) {
          console.log('Checking designJson for nested undefined values...');
          const checkForUndefined = (obj, path = 'designJson') => {
            for (const [key, value] of Object.entries(obj)) {
              const currentPath = `${path}.${key}`;
              if (value === undefined) {
                console.error(`Found undefined at: ${currentPath}`);
              } else if (value && typeof value === 'object' && !Array.isArray(value)) {
                checkForUndefined(value, currentPath);
              } else if (Array.isArray(value)) {
                value.forEach((item, idx) => {
                  if (item === undefined) {
                    console.error(`Found undefined at: ${currentPath}[${idx}]`);
                  } else if (item && typeof item === 'object') {
                    checkForUndefined(item, `${currentPath}[${idx}]`);
                  }
                });
              }
            }
          };
          checkForUndefined(newTemplateData.designJson);
        }
        
        // Check for any undefined values
        const undefinedKeys = Object.keys(newTemplateData).filter(key => 
          newTemplateData[key] === undefined
        );
        if (undefinedKeys.length > 0) {
          throw new Error(`Found undefined values in fields: ${undefinedKeys.join(', ')}`);
        }
        
        const templateId = generateTemplateId(formState.name);
        await setDoc(
          doc(db, collectionPath('emailTemplates'), templateId),
          newTemplateData
        );
        
        // Update local state including the new ID
        setFormState(prev => ({
          ...prev,
          id: templateId,
          htmlContent: htmlToSave,
          designJson: designToSave,
          variables: variablesArray
        }));
        
        onSaved('Template created successfully');
        await logUserAction({ action: 'Email Template Created', details: `Template created: "${formState.name}"`, entityType: 'EmailTemplate', entityId: templateId });
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderPreview = () => {
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 border-b">
          <div className="text-sm text-gray-600">
            <strong>Subject:</strong> {formState.subject.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
              const variable = availableVariables.find(v => v.name === varName);
              return variable ? variable.example : match;
            })}
          </div>
          {formState.preheader && (
            <div className="text-xs text-gray-500 mt-1">
              <strong>Preheader:</strong> {formState.preheader}
            </div>
          )}
        </div>
        <iframe
          srcDoc={previewHtml || formState.htmlContent}
          className="w-full bg-white"
          style={{ height: isFullscreen ? 'calc(100vh - 450px)' : '24rem' }}
          title="Email Preview"
          sandbox="allow-same-origin allow-scripts"
        />
      </div>
    );
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${isFullscreen ? 'z-[9999]' : 'z-50'} ${isFullscreen ? 'p-0' : 'p-4'}`}>
      <div className={`bg-white rounded-lg shadow-xl w-full flex flex-col ${isFullscreen ? 'h-screen max-w-full rounded-none' : 'max-w-6xl max-h-[90vh]'}`}>
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <h3 className="text-xl font-bold text-gray-900">
            {template?.id ? 'Edit Template' : 'Create Template'}
            {syncing && <span className="ml-3 text-sm text-gray-500 animate-pulse">Syncing...</span>}
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleFullscreen}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              {isFullscreen ? '⊟ Exit Fullscreen' : '⊡ Fullscreen'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-grow overflow-y-auto ${isFullscreen ? 'px-2 py-2' : 'px-6 py-4'}`}>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Basic Info */}
          {!isFullscreen && (
            <>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={formState.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Spring Collection Announcement"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <div className="flex gap-2">
                  <select
                    className="flex-grow border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    value={formState.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    disabled={loadingCategories}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                  {canViewAllEmail(currentUser, can) && can(FEATURE_PERMISSIONS.EMAIL_CREATE_TEMPLATE) && <button
                    type="button"
                    onClick={() => setShowAddCategory(!showAddCategory)}
                    className="px-3 py-2 border border-indigo-500 text-indigo-600 rounded hover:bg-indigo-50 transition-colors flex items-center justify-center"
                    title="Add custom category (team-wide)"
                    disabled={loadingCategories}
                  >
                    <span className="text-lg font-bold">+</span>
                  </button>}
                </div>
                
                {/* Add Category Form */}
                {showAddCategory && (
                  <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      New Category Name (visible to entire team)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="flex-grow border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCategory();
                          } else if (e.key === 'Escape') {
                            setShowAddCategory(false);
                            setNewCategoryName('');
                          }
                        }}
                        placeholder="e.g., Seasonal, Event"
                        maxLength={30}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddCategory(false);
                          setNewCategoryName('');
                          setError('');
                        }}
                        className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Press Enter to add • Escape to cancel • Category will be shared with all team members
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={formState.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe the purpose and use case for this template..."
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional: Explain the intent and best use cases for this template
                </p>
              </div>
            </div>

            {/* Privacy Setting */}
            <div className="mb-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formState.isPrivate || false}
                  onChange={(e) => handleInputChange('isPrivate', e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Private Template
                </span>
                <span className="text-xs text-gray-500">
                  (Only visible to you in "My Templates" filter)
                </span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject Line *
                </label>
                <input
                  type="text"
                  ref={subjectRef}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={formState.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  onFocus={trackCursor('subject')}
                  onSelect={trackCursor('subject')}
                  onBlur={trackCursor('subject')}
                  placeholder="e.g., {{customerName}}, check out our new collection!"
                  maxLength={200}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preheader Text
                </label>
                <input
                  type="text"
                  ref={preheaderRef}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={formState.preheader}
                  onChange={(e) => handleInputChange('preheader', e.target.value)}
                  onFocus={trackCursor('preheader')}
                  onSelect={trackCursor('preheader')}
                  onBlur={trackCursor('preheader')}
                  placeholder="Preview text shown in email clients (optional)"
                  maxLength={200}
                />
              </div>
            </div>
            </>
          )}

          {/* Variables Panel */}
          {!isFullscreen && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-blue-900">Available Variables</h4>
              <button
                onClick={() => setShowVariableMenu(!showVariableMenu)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {showVariableMenu ? 'Hide' : 'Show'} Variables
              </button>
            </div>

            {showVariableMenu && (
              <>
                <p className="text-xs text-blue-700">
                  Click to insert at your cursor currently in <strong>{FIELD_LABELS[activeInsertField]}</strong>, or drag a variable
                  into any field.
                </p>
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {availableVariables.map(v => (
                    <button
                      key={v.name}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', `{{${v.name}}}`);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onClick={() => insertVariable(v.name)}
                      className="text-xs bg-white border border-blue-300 rounded px-2 py-1 hover:bg-blue-100 cursor-grab active:cursor-grabbing transition-colors"
                      title={`Example: ${v.example}`}
                    >
                      {'{'}{'{'}{v.name}{'}'}{'}'} 
                    </button>
                  ))}
                </div>
              </>
            )}
            
            {usedVariables.length > 0 && (
              <div className="mt-2 text-xs text-blue-700">
                <strong>Used in this template:</strong> {usedVariables.join(', ')}
              </div>
            )}

            {unknownVariables.length > 0 && (
              <div className="mt-3 bg-red-50 border border-red-300 text-red-800 rounded px-3 py-2 text-xs">
                <strong>⚠️ Unrecognized variables — fix these before the template can be saved:</strong>
                <ul className="mt-1 space-y-0.5">
                  {unknownVariables.map(u => (
                    <li key={u.name}>
                      <code className="bg-white px-1 rounded">{`{{${u.name}}}`}</code>
                      {u.suggestion && <> — did you mean <code className="bg-white px-1 rounded">{`{{${u.suggestion}}}`}</code>?</>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          )}

          {/* Editor Mode Tabs */}
          <div className="border-b mb-4 flex space-x-4">
            {[
              { id: 'design', label: 'Design (WYSIWYG)', icon: '🎨' },
              { id: 'code', label: 'HTML Code', icon: '📝' },
              { id: 'preview', label: 'Preview', icon: '👁️' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                disabled={syncing}
                className={`px-4 py-2 font-medium transition-colors ${
                  editorMode === tab.id
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                } ${syncing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Editor Content */}
          <div className="mb-4">
            {/* Keep editor mounted but hide when not in design mode to prevent reloading */}
            <div style={{ 
              visibility: editorMode === 'design' ? 'visible' : 'hidden',
              height: editorMode === 'design' ? 'auto' : '0',
              overflow: editorMode === 'design' ? 'visible' : 'hidden'
            }}>
              <div>
                {!formState.designJson && template?.id && (
                  <div className="mb-3 bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded text-sm">
                    ⚠️ <strong>HTML-Only Template:</strong> This template was created using HTML Code only. 
                    The WYSIWYG editor is empty. To use the visual editor, you'll need to rebuild the design from scratch 
                    or continue editing in the HTML Code tab.
                  </div>
                )}
                <div className="text-sm text-gray-600 mb-2">
                  Drag and drop email builder with images, buttons, and custom HTML blocks.
                  <span className="ml-2 text-amber-600">
                    💡 Design changes sync to Code/Preview. Code edits won't sync back to Design.
                  </span>
                </div>
                <div 
                  ref={editorContainerRef}
                  className="border border-gray-300 rounded-lg overflow-hidden flex flex-col" 
                  style={{ 
                    height: isFullscreen ? 'calc(100vh - 180px)' : '600px',
                    minHeight: isFullscreen ? 'calc(100vh - 180px)' : '600px'
                  }}
                >
                  <EmailEditor
                    ref={emailEditorRef}
                    onLoad={onEditorLoad}
                    options={{
                      projectId: 1234, // Free tier project ID
                      displayMode: 'email',
                      tools: {
                        image: {
                          enabled: true
                        }
                      },
                      mergeTags: availableVariables.map(v => ({
                        name: v.label,
                        value: `{{${v.name}}}`,
                        sample: v.example
                      }))
                    }}
                    style={{ 
                      height: '100%',
                      width: '100%'
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Use the merge tags menu to insert variables like Customer Name, Contact Name, etc.
                </p>
              </div>
            </div>

            {editorMode === 'code' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    HTML Content *
                  </label>
                  <button
                    onClick={() => setFormState(prev => ({ ...prev, htmlContent: defaultTemplate }))}
                    className="text-xs text-indigo-600 hover:text-indigo-700"
                  >
                    Reset to Default Template
                  </button>
                </div>
                <textarea
                  ref={htmlEditorRef}
                  className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={formState.htmlContent}
                  onChange={(e) => handleInputChange('htmlContent', e.target.value)}
                  onFocus={trackCursor('htmlContent')}
                  onSelect={trackCursor('htmlContent')}
                  onBlur={trackCursor('htmlContent')}
                  rows={isFullscreen ? 35 : 20}
                  placeholder="Enter HTML content here..."
                />
                <div className="text-xs text-gray-500 mt-1 space-y-1">
                  <p>💡 You can use HTML, CSS, and variables like {'{'}{'{'} customerName {'}'}{'}'}.</p>
                  <p className="text-amber-600">
                    ⚠️ Note: HTML changes here will appear in Preview but won't automatically convert back to the Design (WYSIWYG) view. 
                    Choose either Design mode OR Code mode for best results.
                  </p>
                </div>
              </div>
            )}

            {editorMode === 'preview' && renderPreview()}
          </div>

          {/* Text Version Section - Always visible below editor */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Plain Text Version (Optional)
              </label>
              <button
                onClick={generateTextFromHtml}
                className="text-xs text-indigo-600 hover:text-indigo-700"
              >
                Auto-generate from HTML
              </button>
            </div>
            <textarea
              ref={textEditorRef}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={formState.textContent}
              onChange={(e) => handleInputChange('textContent', e.target.value)}
              onFocus={trackCursor('textContent')}
              onSelect={trackCursor('textContent')}
              onBlur={trackCursor('textContent')}
              rows={6}
              placeholder="Plain text fallback for email clients that don't support HTML"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {usedVariables.length > 0 && (
              <span>{usedVariables.length} variable(s) detected</span>
            )}
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            
            {template?.id && (
              <button
                onClick={handleSendTest}
                disabled={saving}
                className="px-4 py-2 border border-indigo-600 rounded text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                📧 Send Test
              </button>
            )}
            
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : template?.id ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
